/**
 * <quantity-breaks>
 *
 * Tier selector that lives inside <product-form>.
 * - Updates the form's hidden `quantity` input on tier change
 * - Updates the displayed price on the in-form ATC button AND the cart-bar
 * - Optionally appends a line item property recording the chosen tier
 * - Optionally redirects through `/discount/<CODE>/cart/add?...` so the
 *   merchant's Shopify automatic discount applies in one tap.
 *
 * Listens to: theme.PUB_SUB_EVENTS.variantChange (so price re-syncs on variant swap)
 * Coexists with: <product-form>, <cart-bar>, <product-info>
 */
(function () {
  if (customElements.get('quantity-breaks')) return;

  var formatMoney = (window.theme && window.theme.Currency && window.theme.Currency.formatMoney)
    || function (cents) {
        var amount = (Math.round(cents) / 100).toFixed(2);
        return '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      };

  var moneyFormat = (window.theme && window.theme.moneyFormat) || '${{amount}}';

  function fmt(cents) {
    try { return formatMoney(cents, moneyFormat); }
    catch (e) { return formatMoney(cents); }
  }

  class QuantityBreaks extends HTMLElement {
    constructor() {
      super();
      this.options = [];
      this.currentVariantPrice = parseInt(this.dataset.basePrice, 10) || 0;
      this.currentCompareAt = parseInt(this.dataset.baseCompareAt, 10) || 0;
      this.sectionId = this.dataset.sectionId || '';
      this.discountUrlBehavior = this.dataset.discountBehavior || 'attribute'; // 'redirect' | 'attribute' | 'none'
      this.propertyLabel = this.dataset.propertyLabel || 'Bundle';
      this.handleTierChange = this.handleTierChange.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleCartBarClick = this.handleCartBarClick.bind(this);
    }

    connectedCallback() {
      this.parseOptions();

      this.tierInputs = Array.from(this.querySelectorAll('[data-qb-input]'));
      this.tierCards = Array.from(this.querySelectorAll('[data-qb-card]'));
      this.productForm = this.closest('product-form') || document.querySelector('product-form[data-section-id="' + this.sectionId + '"]');
      this.formEl = this.productForm ? this.productForm.querySelector('form[data-product-form]') : null;
      this.qtyInput = this.formEl ? this.formEl.querySelector('[name="quantity"]') : null;
      this.atcButton = this.formEl ? this.formEl.querySelector('[data-add-to-cart]') : null;
      this.priceTargets = this.collectPriceTargets();
      this.savingsLabel = this.querySelector('[data-qb-total-saved]');
      this.tierProperty = this.ensureTierPropertyInput();

      this.tierInputs.forEach((input) => input.addEventListener('change', this.handleTierChange));
      if (this.formEl) this.formEl.addEventListener('submit', this.handleFormSubmit, true);

      // Mirror cart-bar one-click behavior to honor the chosen tier.
      var cartBarBtn = document.querySelector('[data-cart-bar-add-to-cart]');
      if (cartBarBtn) cartBarBtn.addEventListener('click', this.handleCartBarClick, true);

      // Sync to variant changes from <product-info>
      if (window.theme && window.theme.PUB_SUB_EVENTS && window.subscribe) {
        this.unsubscribeVariant = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
      }
      document.addEventListener('theme:variant:change', this.handleVariantChange);

      // Initial paint with default-checked tier
      var defaultInput = this.tierInputs.find((i) => i.checked) || this.tierInputs[0];
      if (defaultInput) {
        defaultInput.checked = true;
        this.applyTier(this.optionForInput(defaultInput));
      }
    }

    disconnectedCallback() {
      if (this.unsubscribeVariant) this.unsubscribeVariant();
      document.removeEventListener('theme:variant:change', this.handleVariantChange);
    }

    parseOptions() {
      try {
        var raw = this.querySelector('script[data-qb-options]');
        if (raw) this.options = JSON.parse(raw.textContent.trim());
      } catch (e) {
        this.options = [];
      }
    }

    optionForInput(input) {
      var idx = parseInt(input.dataset.qbIndex, 10);
      return this.options[idx] || { quantity: 1, discount_pct: 0 };
    }

    collectPriceTargets() {
      var targets = [];
      // ATC button price
      if (this.atcButton) {
        var inFormPrice = this.atcButton.querySelector('[data-product-price]');
        if (inFormPrice) targets.push(inFormPrice);
      }
      // Cart-bar price (mobile sticky)
      var cartBarPrices = document.querySelectorAll('cart-bar [data-product-price]');
      cartBarPrices.forEach((el) => targets.push(el));
      // Any opt-in extra price mirrors (e.g. floating CTA)
      document.querySelectorAll('[data-cro-price-mirror]').forEach((el) => targets.push(el));
      return targets;
    }

    ensureTierPropertyInput() {
      if (!this.formEl) return null;
      var existing = this.formEl.querySelector('input[data-qb-tier-property]');
      if (existing) return existing;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + this.propertyLabel + ']';
      input.dataset.qbTierProperty = '';
      this.formEl.appendChild(input);
      return input;
    }

    handleTierChange(event) {
      var option = this.optionForInput(event.target);
      this.tierCards.forEach((card) => card.classList.toggle('is-selected', card.dataset.qbCard === event.target.value));
      this.applyTier(option);
    }

    handleVariantChange(event) {
      // event from PUB_SUB or our custom dispatch
      var variant = (event && event.data && event.data.variant) || (event && event.detail && event.detail.variant);
      if (!variant) return;
      this.currentVariantPrice = variant.price;
      this.currentCompareAt = variant.compare_at_price || 0;
      var checked = this.tierInputs.find((i) => i.checked) || this.tierInputs[0];
      if (checked) this.applyTier(this.optionForInput(checked));
      this.refreshTierLabels();
    }

    refreshTierLabels() {
      // Update the per-tier cents-each + total displayed inside each card
      this.tierCards.forEach((card) => {
        var idx = parseInt(card.dataset.qbIndex, 10);
        var opt = this.options[idx];
        if (!opt) return;
        var lineTotal = this.lineTotal(opt);
        var perEach = Math.round(lineTotal / opt.quantity);
        var savings = this.lineSavings(opt);

        var totalEl = card.querySelector('[data-qb-total]');
        var perEl = card.querySelector('[data-qb-per]');
        var saveEl = card.querySelector('[data-qb-save]');
        if (totalEl) totalEl.textContent = fmt(lineTotal);
        if (perEl) perEl.textContent = fmt(perEach);
        if (saveEl && savings > 0) saveEl.textContent = saveEl.dataset.template
          ? saveEl.dataset.template.replace('[amount]', fmt(savings))
          : fmt(savings);
      });
    }

    lineTotal(option) {
      // 1. If the merchant set an explicit fixed price (in cents), use it verbatim.
      var fixed = parseInt(option.fixed_price, 10);
      if (fixed && fixed > 0) return fixed;
      // 2. Otherwise compute from regular price x qty minus discount %.
      var raw = this.currentVariantPrice * option.quantity;
      var pct = parseFloat(option.discount_pct) || 0;
      return Math.round(raw * (1 - pct / 100));
    }

    lineSavings(option) {
      var raw = this.currentVariantPrice * option.quantity;
      return raw - this.lineTotal(option);
    }

    applyTier(option) {
      if (!option) return;
      this.activeOption = option;

      // Update hidden quantity input on the form
      if (this.qtyInput) {
        this.qtyInput.value = option.quantity;
        // Keep <quantity-input> web component (if present) in sync visually.
        var event = new Event('change', { bubbles: true });
        this.qtyInput.dispatchEvent(event);
      }

      // Update the displayed price (button + cart bar)
      var displayPrice = this.lineTotal(option);
      this.priceTargets.forEach((target) => {
        target.innerHTML = '';
        var span = document.createElement('span');
        span.textContent = fmt(displayPrice);
        target.appendChild(span);

        // If there's a real discount, show strikethrough next to it.
        if (option.discount_pct > 0 || (this.currentCompareAt && this.currentCompareAt > this.currentVariantPrice)) {
          var compareTotal = (this.currentCompareAt || this.currentVariantPrice) * option.quantity;
          if (compareTotal > displayPrice) {
            var s = document.createElement('s');
            s.textContent = fmt(compareTotal);
            target.appendChild(document.createTextNode(' '));
            target.appendChild(s);
          }
        }
      });

      // Update tier property line item
      if (this.tierProperty) this.tierProperty.value = option.label || ('Buy ' + option.quantity);

      // Update savings caption
      if (this.savingsLabel) {
        var save = this.lineSavings(option);
        if (save > 0) {
          this.savingsLabel.hidden = false;
          this.savingsLabel.textContent = (this.savingsLabel.dataset.template || 'You save [amount]')
            .replace('[amount]', fmt(save));
        } else {
          this.savingsLabel.hidden = true;
        }
      }

      this.refreshTierLabels();
    }

    handleFormSubmit(event) {
      var option = this.activeOption;
      if (!option) return;
      // Discount-redirect mode: intercept and route through /discount/CODE/cart/add
      if (this.discountUrlBehavior === 'redirect' && option.discount_code) {
        event.preventDefault();
        var variantId = (this.formEl.querySelector('[name="id"]') || {}).value;
        var url = '/discount/' + encodeURIComponent(option.discount_code)
          + '/cart/add?id=' + encodeURIComponent(variantId)
          + '&quantity=' + encodeURIComponent(option.quantity);
        window.location.href = url;
      }
      // 'attribute' mode just lets the form submit normally; the line item property
      // we appended will surface the chosen tier; the merchant's Shopify automatic
      // discount (cart-rule driven by quantity) applies at checkout.
    }

    handleCartBarClick(event) {
      // The cart-bar's "add to cart" forwards click to the main ATC button.
      // Make sure the latest tier is reflected on the form before that happens.
      var option = this.activeOption;
      if (!option) return;
      if (this.qtyInput) this.qtyInput.value = option.quantity;
      if (this.tierProperty) this.tierProperty.value = option.label || ('Buy ' + option.quantity);
      // If we're in redirect mode AND the cart-bar is direct-add (no variant picker),
      // the form submit handler above will catch it because the cart-bar dispatches
      // a synthetic click on the real ATC button.
    }
  }

  customElements.define('quantity-breaks', QuantityBreaks);
})();
